import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { activityFeedApi } from '../utils/activityFeedApi';
import { getActiveRemindersCount } from '../utils/remindersData';

const ActivityFeedContext = createContext();

export const useActivityFeed = () => {
  const context = useContext(ActivityFeedContext);
  if (!context) {
    throw new Error('useActivityFeed must be used within ActivityFeedProvider');
  }
  return context;
};

export const ActivityFeedProvider = ({ children }) => {
  const [activities, setActivities] = useState([]);
  const [counts, setCounts] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Load activity feed data
  const loadActivityData = useCallback(async (silent = false) => {
    // If silent refresh, don't show loading state
    if (!silent) {
      setIsLoading(true);
    } else {
      setIsRefreshing(true);
    }
    
    try {
      console.log('🔄 Loading activity feed data in background...');
      
      // Fetch activity feed data
      const activityData = await activityFeedApi.getActivityFeed();
      
      // Get actual reminders count
      const actualActiveReminders = getActiveRemindersCount();
      
      // Extract activities array
      if (activityData && activityData.success && Array.isArray(activityData.activities)) {
        setActivities(activityData.activities);
      } else if (activityData && Array.isArray(activityData.activities)) {
        setActivities(activityData.activities);
      } else {
        setActivities([]);
      }
      
      // Use catalog data from activityFeedApi instead of making another API call
      let unreleasedSongsCount = 0;
      if (activityData.success && activityData.counts.catalog) {
        unreleasedSongsCount = activityData.counts.catalog;
      }
      
      // Use real data from API, fallback to 0 if not available
      const countsData = {
        activeReminders: actualActiveReminders,
        pendingBookings: activityData.success ? (activityData.counts.bookings || 0) : 0,
        catalogItems: unreleasedSongsCount,
        merchItems: activityData.success ? (activityData.counts.merch || 0) : 0,
        playlistAdds: activityData.success ? (activityData.counts.playlist || 0) : 0,
        chartAdds: activityData.success ? (activityData.counts.charting || 0) : 0
      };
      
      setCounts(countsData);
      setLastUpdated(Date.now());
      
      console.log('✅ Activity feed data loaded:', {
        activitiesCount: activityData?.activities?.length || 0,
        counts: countsData
      });
    } catch (error) {
      console.error('❌ Failed to load activity feed data:', error);
      
      // Fallback to at least getting the correct reminders count
      const actualActiveReminders = getActiveRemindersCount();
      setCounts({
        activeReminders: actualActiveReminders,
        pendingBookings: 0,
        catalogItems: 0,
        merchItems: 0,
        playlistAdds: 0,
        chartAdds: 0
      });
      setActivities([]);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  // Initial load on mount
  useEffect(() => {
    loadActivityData(false);
  }, [loadActivityData]);

  // Background refresh every 2 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      if (!document.hidden) {
        console.log('⏰ Auto-refreshing activity feed...');
        loadActivityData(true); // Silent refresh
      }
    }, 120000); // 2 minutes
    
    return () => clearInterval(interval);
  }, [loadActivityData]);

  // Refresh when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('👀 Tab visible, refreshing activity feed...');
        loadActivityData(true); // Silent refresh
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [loadActivityData]);

  // Listen for reminder updates to refresh dashboard
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'dizee_reminders') {
        console.log('📡 Activity feed detected reminder changes, refreshing...');
        loadActivityData(true); // Silent refresh
      }
    };

    const handleCustomStorageEvent = (e) => {
      if (e.detail?.key === 'dizee_reminders') {
        console.log('📡 Activity feed detected custom reminder changes, refreshing...');
        loadActivityData(true); // Silent refresh
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('localStorageUpdate', handleCustomStorageEvent);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageUpdate', handleCustomStorageEvent);
    };
  }, [loadActivityData]);

  // Manual refresh function for components
  const refresh = useCallback(() => {
    loadActivityData(false);
  }, [loadActivityData]);

  const value = {
    activities,
    counts,
    isLoading,
    isRefreshing,
    lastUpdated,
    refresh
  };

  return (
    <ActivityFeedContext.Provider value={value}>
      {children}
    </ActivityFeedContext.Provider>
  );
};

